import { LoadingIndicator } from './ui/Primitives';
import React, { useEffect, useRef, useState } from 'react';
import { beginGoogleChallenge, googleAuthRequest } from '../services/googleAuthApi';
import './GoogleSignInButton.css';

let googleLibrary;
function loadGoogle() {
  if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);
  if (!googleLibrary) googleLibrary = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => resolve(window.google.accounts.id);
    script.onerror = () => { script.remove(); googleLibrary = null; reject(new Error('Google could not load. Check your connection and try again.')); };
    document.head.appendChild(script);
  });
  return googleLibrary;
}

export default function GoogleSignInButton({ purpose = 'signin', payload = {}, onSuccess, disabled = false, divider = false }) {
  const container = useRef(null);
  const latest = useRef({ payload, onSuccess, disabled });
  latest.current = { payload, onSuccess, disabled };
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    let submitting = false;
    let expires;
    setError('');
    setReady(false);
    setBusy(false);
    const node = container.current;
    if (node) node.replaceChildren();
    async function initialize() {
      try {
        const [google, challenge] = await Promise.all([loadGoogle(), beginGoogleChallenge(purpose)]);
        if (!active) return;
        google.initialize({
          client_id: challenge.clientId, nonce: challenge.nonce, auto_select: false,
          ux_mode: 'popup', context: purpose === 'signup' ? 'signup' : 'signin',
          callback: async result => {
            if (!active || submitting || latest.current.disabled) return;
            submitting = true;
            clearTimeout(expires);
            setBusy(true);
            try {
              const response = await googleAuthRequest('google', {
                authenticated: ['link', 'password', 'delete'].includes(purpose),
                body: { ...latest.current.payload, credential: result.credential, challengeId: challenge.challengeId, proof: challenge.proof },
              });
              if (active) latest.current.onSuccess(response);
            } catch (failure) {
              if (active) setError(failure.message);
            } finally {
              if (active) { setBusy(false); setReady(false); node.replaceChildren(); }
            }
          },
        });
        google.renderButton(node, { theme: 'outline', size: 'large', text: 'continue_with', shape: 'pill', width: Math.min(360, node.clientWidth || 280) });
        setReady(true);
        expires = setTimeout(() => { if (active && !submitting) setAttempt(value => value + 1); }, 9 * 60 * 1000);
      } catch (failure) {
        if (active) setError(failure.message);
      }
    }
    initialize();
    return () => { active = false; clearTimeout(expires); if (node) node.replaceChildren(); };
  }, [purpose, attempt]);

  return <div className="google-signin">
    <div ref={container} className={disabled || busy ? 'google-signin-disabled' : ''} aria-busy={busy || (!ready && !error)} inert={disabled || busy ? '' : undefined} />
    {busy ? <LoadingIndicator label="Signing in securely…" /> : null}
    {error ? <div className="google-signin-error" role="alert"><p>{error}</p><button type="button" onClick={() => setAttempt(value => value + 1)}>Try Google again</button></div> : null}
    {divider ? <div className="google-signin-divider"><span>or use email</span></div> : null}
  </div>;
}
